/**
 * S3 업로더 (P5-①) — kingdoo-editor의 s3-uploader.ts 이식(순수 JS, 디버그 비콘 제거 R7).
 * Presigned URL GET → S3 PUT. SSRF 방지 URL 검증, 파일명 변환/UUID/확장자, 진행률 보간, 타임아웃, 재시도.
 *
 * @typedef {Object} S3UploaderConfig
 *  apiEndpoint, env('production'|'development'), path,
 *  fileNameTransform?(nameWithoutExt,file)→string, appendUUID?, preserveExtension?(기본 true),
 *  onProgress?(percent 0–100), uploadTimeoutMs?(기본 120000), maxRetries?(기본 2)
 */

/** S3 URL 검증 — HTTPS 강제 + 사설/메타데이터 IP 차단(SSRF 방지) */
function validateS3Url(url, fieldName) {
  if (typeof url !== 'string' || !url || url.trim() === '') {
    throw new Error(`${fieldName} is required and must be a non-empty string`);
  }
  if (!url.startsWith('https://')) {
    throw new Error(`${fieldName} must use HTTPS protocol`);
  }
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    if (
      hostname === 'localhost' ||
      hostname.startsWith('127.') ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname === '169.254.169.254'
    ) {
      throw new Error(`${fieldName} cannot point to internal/private networks`);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('cannot point to'))
      throw error;
    throw new Error(`${fieldName} is not a valid URL format`);
  }
  return url;
}

export function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID)
    return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function createS3Uploader(config) {
  const {
    apiEndpoint,
    env,
    path,
    fileNameTransform,
    appendUUID,
    preserveExtension = true,
    onProgress,
    uploadTimeoutMs = 120000,
    maxRetries = 2,
  } = config || {};

  if (!apiEndpoint || apiEndpoint.trim() === '')
    throw new Error('apiEndpoint is required for S3 upload.');
  if (!env)
    throw new Error("env is required. Must be 'development' or 'production'.");
  if (!path || path.trim() === '')
    throw new Error('path is required and cannot be empty.');

  const generateHierarchicalFileName = (file) => {
    const originalName = file.name;
    const lastDotIndex = originalName.lastIndexOf('.');
    const nameWithoutExt =
      lastDotIndex === -1
        ? originalName
        : originalName.substring(0, lastDotIndex);
    const extension =
      lastDotIndex === -1 ? '' : originalName.substring(lastDotIndex);

    let filename = nameWithoutExt;
    if (fileNameTransform) filename = fileNameTransform(filename, file);
    if (appendUUID) filename = `${filename}_${generateUUID()}`;
    if (preserveExtension) filename = `${filename}${extension}`;
    return `${env}/${path}/${filename}`;
  };

  return async (file) => {
    const fileName = generateHierarchicalFileName(file);
    const contentType = file.type || 'application/octet-stream';
    const presignedUrlFull = `${apiEndpoint}?key=${encodeURIComponent(fileName)}&contentType=${encodeURIComponent(contentType)}`;

    const response = await fetch(presignedUrlFull);
    if (!response.ok) {
      const errorText = (await response.text().catch(() => '')) || '';
      throw new Error(
        `Failed to get presigned URL: ${response.statusText}, ${errorText}`,
      );
    }
    const { presignedUrl, publicUrl } = await response.json();
    const validatedPresignedUrl = validateS3Url(presignedUrl, 'presignedUrl');
    const validatedPublicUrl = validateS3Url(publicUrl, 'publicUrl');

    let lastError;
    const attempts = maxRetries + 1;
    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        if (onProgress && typeof XMLHttpRequest !== 'undefined') {
          await putWithProgress(
            validatedPresignedUrl,
            file,
            uploadTimeoutMs,
            onProgress,
          );
        } else {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            uploadTimeoutMs,
          );
          const uploadResponse = await fetch(validatedPresignedUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
            body: file,
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (!uploadResponse.ok)
            throw new Error(
              `Failed to upload file: ${uploadResponse.statusText}`,
            );
        }
        return validatedPublicUrl;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt >= attempts - 1) throw lastError;
      }
    }
    throw lastError || new Error('Upload failed');
  };
}

/** XHR PUT + 진행률 보간(progress 이벤트가 드물 때 중간 % 표시) */
function putWithProgress(url, file, timeoutMs, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = timeoutMs;
    let lastReported = -1;
    const REPORT_INTERVAL_MS = 100;
    const simulatedCap = 90;
    let simulatedPercent = 0;

    const simId = setInterval(() => {
      if (simulatedPercent >= simulatedCap) return;
      simulatedPercent = Math.min(simulatedCap, simulatedPercent + 3);
      const p = Math.min(100, simulatedPercent);
      if (p > lastReported) {
        lastReported = p;
        onProgress(p);
      }
    }, REPORT_INTERVAL_MS);
    const clearSim = () => clearInterval(simId);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const p = Math.min(100, Math.round((e.loaded / e.total) * 100));
        if (p >= simulatedCap) clearSim();
        const toReport = Math.max(p, simulatedPercent);
        if (toReport > lastReported) {
          lastReported = toReport;
          onProgress(toReport);
        }
      }
    };
    xhr.onload = () => {
      clearSim();
      if (xhr.status >= 200 && xhr.status < 300) {
        if (lastReported < 100) onProgress(100);
        resolve();
      } else reject(new Error(`Failed to upload file: ${xhr.statusText}`));
    };
    xhr.onerror = () => {
      clearSim();
      reject(new Error('Upload failed'));
    };
    xhr.ontimeout = () => {
      clearSim();
      reject(new Error('Upload timeout'));
    };
    xhr.open('PUT', url);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream',
    );
    onProgress(0);
    lastReported = 0;
    xhr.send(file);
  });
}
